import { useEffect, useState } from 'react';
import { DownloadSimple, Package, Tag, X } from 'shared/icons';
import api from '../../services/api';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { DateText, DateTimeText, Mono, Sku } from '../ui/Mono';
import { Skeleton } from '../ui/Skeleton';
import { useToast } from '../ui/Toast';
import { DownloadZplFile, SendZplToPrinter, useZplPrinters, ZplPrinterSelect } from './zplPrinting';

// Mesma moldura do drawer de Localizações (M1): a coluna da direita é sempre a
// ficha do que está selecionado à esquerda.
const PANEL_CLASS =
    'flex h-full min-h-[420px] flex-col rounded-xl border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950';

const DetailRow = ({ term, children }) => (
    <div className="flex items-baseline justify-between gap-3 py-1">
        <dt className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">{term}</dt>
        <dd className="text-right text-sm text-mono-700 dark:text-mono-300">{children}</dd>
    </div>
);

export function LpnStatusBadgeVariant(status) {
    return status === 'CLOSED' ? 'neutral' : 'success';
}

/**
 * Ficha do LPN — "ONDE ESTÁ" + o que tem dentro (GET /warehouse/lpns/:code).
 *
 * É PAINEL LATERAL, não modal, por dois motivos: (1) paridade com a página de
 * Localizações do M1, onde clicar numa linha preenche a coluna da direita — o
 * mesmo gesto tem que dar o mesmo resultado nas duas telas do módulo; (2) a
 * consulta de LPN é comparativa (varrer a lista conferindo posição e itens de
 * vários), e um modal obrigaria a fechar/abrir a cada linha, escondendo
 * justamente a tabela que se está conferindo.
 *
 * A reimpressão usa o MESMO fluxo do M1 (./zplPrinting): impressora de rede
 * cadastrada ou USB desta máquina pelo QZ Tray, e o .zpl baixável.
 */
const LpnDetailPanel = ({ code, label, onClear }) => {
    const toast = useToast();
    const [lpn, setLpn] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [printOpen, setPrintOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const { printers, localPrinters, selectedPrinter, setSelectedPrinter } = useZplPrinters(printOpen);

    useEffect(() => {
        if (!code) {
            setLpn(null);
            setLoadError(null);
            setLoading(false);
            return undefined;
        }
        let alive = true;
        setLoading(true);
        setLoadError(null);
        setPrintOpen(false);
        api.getWarehouseLpn(code)
            .then((data) => {
                if (alive) {
                    setLpn(data || null);
                }
            })
            .catch((error) => {
                if (alive) {
                    setLoadError(error?.message || label('wms.common.load_error'));
                    setLpn(null);
                }
            })
            .finally(() => {
                if (alive) {
                    setLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [code, label]);

    const handlePrint = async () => {
        if (!lpn?.zpl || !selectedPrinter) {
            return;
        }
        setSending(true);
        try {
            const printerName = await SendZplToPrinter({
                target: selectedPrinter,
                printers,
                zpl: lpn.zpl,
                label,
            });
            toast.success(`${label('wms.labels.sent')}${printerName ? ` — ${printerName}` : ''}`);
        } catch (error) {
            if (error?.code === 'NETWORK_ERROR') {
                toast.error(label('wms.collector.error_network'));
            } else {
                toast.error(error);
            }
        } finally {
            setSending(false);
        }
    };

    if (!code) {
        return (
            <aside aria-label={label('wms.lpns.panel_empty_title')} className={PANEL_CLASS}>
                <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
                    <Package size={30} className="text-mono-400" aria-hidden="true" />
                    <p className="text-sm font-medium text-black dark:text-white">
                        {label('wms.lpns.panel_empty_title')}
                    </p>
                    <p className="max-w-[280px] text-sm text-mono-500 dark:text-mono-400">
                        {label('wms.lpns.panel_empty_hint')}
                    </p>
                </div>
            </aside>
        );
    }

    const items = Array.isArray(lpn?.items) ? lpn.items : [];

    return (
        <aside aria-label={code} className={PANEL_CLASS}>
            <header className="flex items-start justify-between gap-3 border-b border-mono-200 p-4 dark:border-mono-800">
                <div className="min-w-0 space-y-1.5">
                    <Mono className="block truncate text-lg font-semibold text-black dark:text-white" title={code}>
                        {code}
                    </Mono>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {lpn?.status ? (
                            <Badge variant={LpnStatusBadgeVariant(lpn.status)}>
                                {label(`wms.lpn_status.${lpn.status}`)}
                            </Badge>
                        ) : null}
                        {lpn?.is_bulky ? <Badge variant="warning">{label('wms.lpn.field.is_bulky')}</Badge> : null}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClear}
                    aria-label={label('wms.panel.clear')}
                    title={label('wms.panel.clear')}
                    className="shrink-0 rounded-lg p-2 transition-colors hover:bg-mono-100 dark:hover:bg-mono-900"
                >
                    <X size={18} className="text-mono-600 dark:text-mono-400" />
                </button>
            </header>

            <div className="flex-1 space-y-5 overflow-y-auto p-4">
                {loading ? (
                    <Skeleton className="h-40 w-full" />
                ) : loadError ? (
                    <p className="text-sm text-danger-600 dark:text-danger-400">{loadError}</p>
                ) : !lpn ? (
                    <p className="text-sm text-mono-500 dark:text-mono-400">{label('wms.lpns.not_found')}</p>
                ) : (
                    <>
                        <section>
                            <h3 className="mb-1 text-sm font-semibold text-black dark:text-white">
                                {label('wms.lpns.where_is_it')}
                            </h3>
                            <dl className="divide-y divide-mono-100 dark:divide-mono-900">
                                <DetailRow term={label('wms.lpn.field.location')}>
                                    <Mono>{lpn.location_code || '—'}</Mono>
                                </DetailRow>
                                <DetailRow term={label('wms.lpn.field.deposit')}>
                                    <Mono>{lpn.deposit_code || '—'}</Mono>
                                </DetailRow>
                                {lpn.origin_type ? (
                                    <DetailRow term={label('wms.lpn.field.origin')}>
                                        {label(`wms.lpn_origin.${lpn.origin_type}`)}
                                        {lpn.origin_id ? (
                                            <>
                                                {' '}
                                                <Mono>{String(lpn.origin_id)}</Mono>
                                            </>
                                        ) : null}
                                    </DetailRow>
                                ) : null}
                                <DetailRow term={label('wms.lpn.field.created_at')}>
                                    <DateTimeText value={lpn.created_at} relative />
                                </DetailRow>
                            </dl>
                        </section>

                        <section>
                            <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-black dark:text-white">
                                <Package size={16} className="text-mono-500" />
                                {label('wms.lpn.field.items')}
                                <span className="text-xs font-normal text-mono-500 dark:text-mono-400">
                                    ({items.length})
                                </span>
                            </h3>
                            {items.length === 0 ? (
                                <p className="rounded-lg border border-dashed border-mono-300 px-3 py-4 text-center text-sm text-mono-500 dark:border-mono-700 dark:text-mono-400">
                                    {label('wms.lpns.items_empty')}
                                </p>
                            ) : (
                                <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                    {items.map((item) => (
                                        <li key={item.id || `${item.product_id}-${item.supplier_lot || ''}`}>
                                            <div className="flex items-start gap-2 py-2">
                                                <span className="min-w-0 flex-1">
                                                    <Sku value={item.sku} className="text-xs font-medium" />
                                                    <span className="block truncate text-xs text-mono-500 dark:text-mono-400">
                                                        {item.name}
                                                    </span>
                                                    {item.supplier_lot ? (
                                                        <span className="block text-xs text-mono-500 dark:text-mono-400">
                                                            {label('wms.lpn.field.supplier_lot')}:{' '}
                                                            <Mono>{item.supplier_lot}</Mono>
                                                        </span>
                                                    ) : null}
                                                    {item.expiry_date ? (
                                                        <span className="block text-xs text-mono-500 dark:text-mono-400">
                                                            {label('wms.lpn.field.expiry_date')}:{' '}
                                                            <DateText value={item.expiry_date} />
                                                        </span>
                                                    ) : null}
                                                </span>
                                                <Mono className="shrink-0 text-sm text-black dark:text-white">
                                                    {item.qty}
                                                </Mono>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        {/* Reimpressão da etiqueta — mesmo fluxo do M1 (rede + USB) */}
                        <section className="space-y-3">
                            {!printOpen ? (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => setPrintOpen(true)}
                                    disabled={!lpn.zpl}
                                >
                                    <Tag size={14} className="mr-1.5" />
                                    {label('wms.lpns.reprint')}
                                </Button>
                            ) : (
                                <>
                                    <ZplPrinterSelect
                                        label={label}
                                        printers={printers}
                                        localPrinters={localPrinters}
                                        value={selectedPrinter}
                                        onChange={setSelectedPrinter}
                                        disabled={sending}
                                    />
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={handlePrint}
                                            loading={sending}
                                            disabled={!selectedPrinter}
                                        >
                                            <Tag size={14} className="mr-1.5" />
                                            {label('wms.labels.send')}
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="secondary"
                                            disabled={sending}
                                            onClick={() => DownloadZplFile(lpn.zpl, `lpn-${lpn.code}.zpl`)}
                                        >
                                            <DownloadSimple size={14} className="mr-1.5" />
                                            {label('wms.labels.download')}
                                        </Button>
                                    </div>
                                </>
                            )}
                            {!lpn.zpl ? (
                                <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.lpns.no_zpl')}</p>
                            ) : null}
                        </section>
                    </>
                )}
            </div>
        </aside>
    );
};

export default LpnDetailPanel;
